import { useState, useCallback } from 'react';

import { isValidEmail } from '../../utils/helpers';

/**
 * Custom hook for auth form validation and state management
 * Reduces boilerplate in login/signup forms
 */
export const useAuthForm = (initialValues = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  /**
   * Handle input change
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  /**
   * Handle input blur (mark as touched)
   */
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  /**
   * Validate email field
   */
  const validateEmail = useCallback((email) => {
    if (!email) {
      return 'Email is required';
    }
    if (!isValidEmail(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  }, []);

  /**
   * Validate password field
   */
  const validatePassword = useCallback((password) => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  }, []);

  /**
   * Validate all fields
   */
  const validate = useCallback(
    (fieldsToValidate = {}) => {
      const newErrors = {};

      Object.keys(fieldsToValidate).forEach((fieldName) => {
        const validator = fieldsToValidate[fieldName];
        const error = validator(values[fieldName]);
        if (error) {
          newErrors[fieldName] = error;
        }
      });

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [values]
  );

  /**
   * Reset form
   */
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  /**
   * Set a specific error
   */
  const setError = useCallback((fieldName, errorMessage) => {
    setErrors((prev) => ({ ...prev, [fieldName]: errorMessage }));
  }, []);

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateEmail,
    validatePassword,
    validate,
    reset,
    setError,
    setValues,
  };
};

export default useAuthForm;
